import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogoMark } from '../components/Logo';
import { BRAND_NAME } from '../brand';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errors';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  // Security audit 2026-09-01 §2.3: an MFA-enrolled account gets a short-lived
  // mfa_token from /auth/login instead of a session; step 2 exchanges it.
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const { login, completeMfaLogin } = useAuth();
  const navigate = useNavigate();

  const goHome = (user) => {
    toast.success('Login successful');
    if (user.role === 'SuperAdmin') {
      navigate('/admin');
    } else if (user.user_type === 'DealershipUser') {
      navigate('/dashboard');
    } else {
      navigate('/vendor/tasks');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);

    try {
      const result = await login(email, password);
      if (result?.mfaRequired) {
        setMfaToken(result.mfaToken);
        setMfaCode('');
        return;
      }
      goHome(result);
    } catch (error) {
      // Persistent inline banner — stays until the user submits again
      setErrorMsg(getErrorMessage(error, 'Login failed. Check your email and password.'));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);
    try {
      const user = await completeMfaLogin(mfaToken, mfaCode.trim());
      goHome(user);
    } catch (error) {
      const status = error?.response?.status;
      setErrorMsg(getErrorMessage(error, 'That code did not work.'));
      if (status === 401 && /start again/i.test(getErrorMessage(error, ''))) {
        setMfaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <LogoMark sizeClass="h-12 w-12" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">{BRAND_NAME}</h1>
          <p className="text-sm text-muted-foreground">Sign in to manage vehicle reconditioning</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 md:p-8">
          {errorMsg && (
            <div
              data-testid="login-error-banner"
              className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800"
              role="alert"
            >
              <svg className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5.636 5.636a9 9 0 1012.728 0 9 9 0 00-12.728 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-900 dark:text-red-200">Login failed</p>
                <p className="text-sm text-red-800 dark:text-red-200 mt-0.5">{errorMsg}</p>
              </div>
              <button
                type="button"
                onClick={() => setErrorMsg(null)}
                className="text-red-700 dark:text-red-200 hover:text-red-900 dark:hover:text-red-100 text-xs font-medium"
                aria-label="Dismiss error"
              >
                ✕
              </button>
            </div>
          )}
          {mfaToken ? (
          <form onSubmit={handleMfaSubmit} data-testid="mfa-form">
            <p className="text-sm text-muted-foreground mb-4">
              Enter the 6-digit code from your authenticator app. Lost your phone? Use one of your recovery codes.
            </p>
            <div className="mb-6">
              <label htmlFor="mfa-code" className="block text-sm font-medium mb-2">Authenticator code</label>
              <input
                id="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                data-testid="mfa-code-input"
                className="w-full h-11 border border-input rounded-lg px-3 bg-background text-foreground tracking-widest focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-shadow"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="mfa-submit-button"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg px-4 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={() => { setMfaToken(null); setMfaCode(''); setErrorMsg(null); }}
              className="mt-3 w-full text-xs font-medium text-blue-600 hover:text-blue-700"
            >
              Back to password
            </button>
          </form>
          ) : (
          <form onSubmit={handleSubmit} data-testid="login-form">
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email-input"
                className="w-full h-11 border border-input rounded-lg px-3 bg-background text-foreground focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-shadow"
                required
              />
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium">
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs font-medium text-blue-600 hover:text-blue-700">
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="password-input"
                className="w-full h-11 border border-input rounded-lg px-3 bg-background text-foreground focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-shadow"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg px-4 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          )}

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-xs font-medium text-blue-600 hover:text-blue-700">
              Forgot your password? Reset it here.
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}